import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
    useCallback,
} from "react";
import api, { TOKEN_KEY } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        // Auth identity is established via the httpOnly auth_token cookie.
        // We ask the server who we are; if cookie is absent/expired, /auth/me
        // returns 401 and we land logged-out.
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            setUser(null);
            // Stale legacy token cleanup
            localStorage.removeItem(TOKEN_KEY);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = useCallback(async (username, password) => {
        // Dual-track auth architecture — INTENTIONAL:
        //   1. Primary: backend sets an httpOnly `auth_token` cookie
        //      (unreadable from JS → XSS-hardened).
        //   2. Fallback: JWT persisted in localStorage so the axios
        //      interceptor can attach a Bearer header when cross-site
        //      cookies are blocked (Safari ITP; Chrome's third-party
        //      cookie restrictions when the custom domain `pro-pac-pa.com`
        //      hits the API at `emergent.host`).
        // Removing the localStorage half breaks login for those users; keep
        // it. The cookie is preferred whenever available.
        const { data } = await api.post("/auth/login", { username, password });
        if (data?.access_token) {
            localStorage.setItem(TOKEN_KEY, data.access_token);
        }
        setUser(data.user);
        return data.user;
    }, []);

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout");
        } catch (err) {
            console.error("Logout call failed (clearing client state anyway):", err);
        }
        // Defensive: even if the server roundtrip failed, dump the local
        // bearer token so this browser tab is fully signed out.
        localStorage.removeItem(TOKEN_KEY);
        setUser(null);
        // Hard reload so any in-memory React Query / page caches are dropped.
        window.location.href = "/login";
    }, []);

    // Memoize context value so consumer components that only read `user`
    // don't re-render on every AuthProvider render.
    const value = useMemo(
        () => ({ user, loading, login, logout, refresh, setUser }),
        [user, loading, login, logout, refresh],
    );

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
}
